import React, { useState, useEffect } from "react";
import { Form, Input, Button, Space, InputNumber } from "antd";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { fetchT, centsToDollar, dollarToCents } from "../../shared/utils/TypedUtil";
import { useNotification } from "../../shared/hooks";
import {
  LargeVolumeDeliveryZone,
  CreateLargeVolumeDeliveryZoneRequest,
  UpdateLargeVolumeDeliveryZoneRequest,
} from "./types";

type ZoneFormProps = {
  restaurantId: string;
  onSuccess: () => void;
  editingZone?: LargeVolumeDeliveryZone | null;
  onCancelEdit?: () => void;
};

export const ZoneForm: React.FC<ZoneFormProps> = ({
  restaurantId,
  onSuccess,
  editingZone,
  onCancelEdit,
}) => {
  const { t } = useTranslation();
  const { createSuccess, updateSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingZone) {
      form.setFieldsValue({
        name: editingZone.name,
        minimum_pretax_price: Number(centsToDollar(editingZone.minimum_pretax_price)),
        delivery_fee: Number(centsToDollar(editingZone.delivery_fee)),
        free_delivery_threshold: Number(centsToDollar(editingZone.free_delivery_threshold)),
      });
    } else {
      form.resetFields();
    }
  }, [editingZone, form]);

  const onFinish = (values: {
    name: string;
    minimum_pretax_price: number;
    delivery_fee: number;
    free_delivery_threshold: number;
  }) => {
    setLoading(true);

    if (editingZone) {
      const request: UpdateLargeVolumeDeliveryZoneRequest = {
        name: values.name,
        minimum_pretax_price: dollarToCents(values.minimum_pretax_price),
        delivery_fee: dollarToCents(values.delivery_fee),
        free_delivery_threshold: dollarToCents(values.free_delivery_threshold),
      };

      fetchT<{ id: string }>(
        "PUT",
        `${API_URL}/v1/r/${restaurantId}/admin/large_volume_delivery_zones/${editingZone.id}`,
        request,
        () => {
          updateSuccess(t("delivery_zone"));
          form.resetFields();
          onSuccess();
          setLoading(false);
        },
        (err) => {
          notifyError(t("failed_to_update_delivery_zone"), err);
          setLoading(false);
        }
      );
    } else {
      const request: CreateLargeVolumeDeliveryZoneRequest = {
        name: values.name,
        minimum_pretax_price: dollarToCents(values.minimum_pretax_price),
        delivery_fee: dollarToCents(values.delivery_fee),
        free_delivery_threshold: dollarToCents(values.free_delivery_threshold),
      };

      fetchT<{ id: string }>(
        "POST",
        `${API_URL}/v1/r/${restaurantId}/admin/large_volume_delivery_zones`,
        request,
        () => {
          createSuccess(t("delivery_zone"));
          form.resetFields();
          onSuccess();
          setLoading(false);
        },
        (err) => {
          notifyError(t("failed_to_create_delivery_zone"), err);
          setLoading(false);
        }
      );
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ minimum_pretax_price: 0, delivery_fee: 0, free_delivery_threshold: 0 }}
    >
      <Form.Item
        name="name"
        label={t("zone_name")}
        rules={[{ required: true, message: t("name_is_required") }]}
      >
        <Input placeholder={t("enter_zone_name")} />
      </Form.Item>

      <Form.Item
        name="minimum_pretax_price"
        label={t("minimum_pretax_price")}
        rules={[{ required: true, message: t("minimum_pretax_price_is_required") }]}
      >
        <InputNumber
          min={0}
          precision={2}
          style={{ width: "100%" }}
          prefix="$"
          placeholder={t("enter_minimum_pretax_price")}
        />
      </Form.Item>

      <Form.Item
        name="delivery_fee"
        label={t("delivery_fee")}
        rules={[{ required: true, message: t("delivery_fee_is_required") }]}
      >
        <InputNumber
          min={0}
          precision={2}
          style={{ width: "100%" }}
          prefix="$"
          placeholder={t("enter_delivery_fee")}
        />
      </Form.Item>

      <Form.Item
        name="free_delivery_threshold"
        label={t("free_delivery_threshold")}
        rules={[{ required: true, message: t("free_delivery_threshold_is_required") }]}
      >
        <InputNumber
          min={0}
          precision={2}
          style={{ width: "100%" }}
          prefix="$"
          placeholder={t("enter_free_delivery_threshold")}
        />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {editingZone ? t("save") : t("create")}
          </Button>
          <Button onClick={handleCancel}>{editingZone ? t("cancel") : t("reset")}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
